import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { SetCreateFlightRuleValueAction, SetFlightUpdateSuccessValuesAction } from '../store/flight/flights-action';
import { ReefRESTService } from '@reef/fwk-utils';
import {
  buildFlightFromCreateOrUpateResponse,
  buildFlightRuleListFromSearchResponse,
  buildFlightRuleFromDeleteResponse
} from './handler/flight-response-handler';
import {
  FlightRuleCreateRequest,
  FlightRuleSearchRequest,
  FlightRuleDeleteRequest,
  FlightRuleUpdateRequest
} from '../flights/model/flight-request';
import { FlightRuleCreateUpdateResponse } from '../flights/model/flight-response';
import { Observable, of, Subscription } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { JourneyUi, initialJourneyUi } from '../flights/model/flight-rule-ui';
import { Router } from '@angular/router';
import { TYPE_SUCCESS } from './model/pos';
import { HistoryRuleRecord } from '../model/history-rule-record';
import { convertHistoryRuleResponseToHistoryRuleUIRecords } from '../util/history-rule-utils';
import { HistoryRuleResponse } from '../model/history-rule-response';
import { ConfigurationService } from './configuration/configuration.service';
import { FeatureFlags } from '../core/util/resources';
import { FlightsState } from '../store/flight/flights-state';

@Injectable({
  providedIn: 'root'
})
export class FlightService implements OnDestroy {
  isFareTypeEnabled = false;

  subscription: Subscription = new Subscription();

  constructor(
    private readonly store: Store<FlightsState>,
    private readonly router: Router,
    private readonly reefRestService: ReefRESTService,
    private readonly config: ConfigurationService
  ) {
    this.subscription.add(
      this.config
        .getParameter$(FeatureFlags.flightsFareType)
        .subscribe((flightsFareTypeEnabled) => (this.isFareTypeEnabled = flightsFareTypeEnabled))
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  createFlightRule(request: FlightRuleCreateRequest): Observable<JourneyUi> {
    return this.reefRestService
      .post(`/journey/v2/rule`, request)
      .pipe(map((response) => this.buildResultForFlightRuleCreation(response)));
  }

  searchFlightRules(
    request: FlightRuleSearchRequest
  ): Observable<{ journeys: JourneyUi[]; pagination?: { totalCount: number; currentPage: number; pageSize: number } }> {
    const requestedPageSize = Number.parseInt(request.searchFollowUpRequest?.numberOfRecords ?? '10', 10);
    return this.reefRestService.post(`/journey/v2/rule/list`, request).pipe(
      map((response) => {
        const resp = { ...response };
        return buildFlightRuleListFromSearchResponse(resp, this.isFareTypeEnabled, requestedPageSize);
      })
    );
  }

  getRuleHistory(id: string): Observable<HistoryRuleRecord[]> {
    return this.reefRestService.get(`/journey/rule/history/${id}`).pipe(
      map((response: HistoryRuleResponse) => convertHistoryRuleResponseToHistoryRuleUIRecords(response)),
      catchError(() => of([]))
    );
  }

  deleteFlightRule(request: FlightRuleDeleteRequest): Observable<JourneyUi> {
    const journeyId = request.journey.rule.id;
    const requestForDelete = this.buildBackendRequestForDelete(request);
    return this.reefRestService
      .delete(`/journey/v2/rule/${journeyId}`, requestForDelete)
      .pipe(map((response) => buildFlightRuleFromDeleteResponse(response, request)));
  }

  updateFlightRule(request: FlightRuleUpdateRequest): Observable<JourneyUi> {
    const flightRuleToUpdate = request.journey.rule.id;
    return this.reefRestService
      .put(`/journey/v2/rule/${flightRuleToUpdate}`, request)
      .pipe(map((response) => this.buildResultForFlightRuleUpdate(response)));
  }

  buildResultForFlightRuleCreation(response: FlightRuleCreateUpdateResponse): JourneyUi {
    const journey = buildFlightFromCreateOrUpateResponse(response, this.isFareTypeEnabled);
    if (journey.statusType === TYPE_SUCCESS) {
      this.store.dispatch(
        new SetCreateFlightRuleValueAction({
          value: initialJourneyUi
        })
      );
    }
    return journey;
  }

  buildBackendRequestForDelete(request: FlightRuleDeleteRequest): FlightRuleDeleteRequest {
    return {
      version: request.version,
      journey: {
        rule: {
          version: request.journey.rule.version
        }
      }
    };
  }

  private buildResultForFlightRuleUpdate(result: FlightRuleCreateUpdateResponse): JourneyUi {
    const journey = buildFlightFromCreateOrUpateResponse(result, this.isFareTypeEnabled);
    if (journey.statusType === TYPE_SUCCESS) {
      this.triggerSuccessActionForFlightModify(journey);
    }
    return journey;
  }

  private triggerSuccessActionForFlightModify(journey: JourneyUi) {
    this.store.dispatch(new SetFlightUpdateSuccessValuesAction({ updatedFlightRule: journey }));
    this.router.navigate(['flights/display/']);
  }
}
